import CloseIcon from "@mui/icons-material/Close";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { Box } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import {
  fetchDistrictList,
  fetchEventlist,
  fetchPincodeList,
  fetchStateList,
} from "../../util/axiosConfig/api/api";
import { decryptWithSpaces } from "../../util/DecryptWithSpaces";
import CustomizedDropdown from "../../util/CustomizedDropdown";
import DspFooter from "./DspFooter";
import DspHeader from "./DspHeader";
import EventCardLarge from "./EventCardLarge";
import EventCardSmall from "./EventCardSmall";
import "./EventsLanding.css";
import SkeletonLoader from "./SkeletonLoader";
import SkeletonSmall from "./SkeletonSmall";
import CustomSelect from "../../util/CustomSelect";

const EventsLanding = () => {
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [todaysEvents, setTodaysEvents] = useState([]);
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [showAllPast, setShowAllPast] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  // Filter parameters (writable so FilterModal can update them)
  const [filters, setFilters] = useState({
    state: "NA",
    city: "NA",
    pincode: "NA",
    date: "NA",
    subject: "NA",
  });

  // Temporary filter state for the dropdown
  const [tempFilters, setTempFilters] = useState({
    month: null,
    state: "",
    city: "",
    pincode: "",
  });

  // Dynamic options for dropdowns
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [pinOptions, setPinOptions] = useState([]);
  const [districtData, setDistrictData] = useState([]); // Store full district data with pincodes

  // Date range for DatePicker (current year -1 to +3 years)
  const minDateRange = dayjs().subtract(1, "years");
  const maxDateRange = dayjs().add(3, "years");

  const handleApplyFilter = () => {
    // Convert temp filters to API format and trigger re-fetch
    let formattedDate = "NA";
    if (tempFilters.month) {
      try {
        // Handle dayjs object - format as MM-YYYY for month filtering
        if (tempFilters.month.$d) {
          formattedDate = moment(tempFilters.month.$d).format("MM-YYYY");
        } else if (tempFilters.month.toDate) {
          formattedDate = moment(tempFilters.month.toDate()).format("MM-YYYY");
        } else {
          formattedDate = moment(tempFilters.month).format("MM-YYYY");
        }
      } catch (error) {
        console.error("Error formatting date:", error);
      }
    }

    setFilters((prev) => ({
      ...prev,
      state: tempFilters.state || "NA",
      city: tempFilters.city || "NA",
      pincode: tempFilters.pincode || "NA",
      date: formattedDate,
    }));
    setShowFilterDropdown(false);
  };

  const handleClearFilter = () => {
    setTempFilters({
      month: null,
      state: "",
      city: "",
      pincode: "",
    });
    setFilters({
      state: "NA",
      city: "NA",
      pincode: "NA",
      date: "NA",
      subject: "NA",
    });
    setCityOptions([]);
    setPinOptions([]);
    setShowFilterDropdown(false);
  };

  const isFilterApplied =
    filters.state !== "NA" ||
    filters.city !== "NA" ||
    filters.pincode !== "NA" ||
    (filters.date !== "NA" && filters.date !== "");

  // Fetch state list on component mount
  useEffect(() => {
    handlefetchStateList();
  }, []);

  const handlefetchStateList = async () => {
    try {
      const res = await fetchStateList();
      if (res) {
        const options = res.map((item) => ({
          value: item,
          label: item,
        }));
        setStateOptions(options);
      }
    } catch (error) {
      console.error("Error fetching state list:", error);
    }
  };

  const handlefetchCityList = async (state) => {
    setCityOptions([]);
    setPinOptions([]);
    try {
      const res = await fetchDistrictList(state);
      if (res && Array.isArray(res)) {
        setDistrictData(res); // Store full data including pincodes
        const options = res.map((item) => ({
          value: item.districtName,
          label: item.districtName,
        }));
        setCityOptions(options);
      }
    } catch (error) {
      console.error("Error fetching city list:", error);
    }
  };

  const handlefetchPincodeList = async (city) => {
    setPinOptions([]);
    try {
      const res = await fetchPincodeList(city);
      if (res) {
        const options = res.map((item) => ({
          value: item,
          label: item,
        }));
        setPinOptions(options);
      }
    } catch (error) {
      console.error("Error fetching pincode list:", error);
    }
  };

  const handleSearch = useCallback(() => {
    // Use `subject` as the search parameter for fetchEventlist
    setFilters((prev) => ({ ...prev, subject: searchQuery || "NA" }));
  }, [searchQuery]);

  // Auto-search with debounce when searchQuery changes
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      handleSearch();
    }, 500); // 500ms delay after user stops typing

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, handleSearch]);

  // Transform API data to match EventCard component expectations
  const determineEventType = (event) => {
    // Check if offline property is explicitly defined
    if (typeof event.offline !== "undefined") {
      const offVal = event.offline;
      const offIsOffline =
        offVal === true ||
        String(offVal).toLowerCase() === "true" ||
        Number(offVal) === 1 ||
        (typeof offVal === "string" &&
          String(offVal).toLowerCase().includes("offline"));
      return offIsOffline ? "offline" : "online";
    }

    // Check venue for online indicator
    if (event.venue?.toLowerCase().includes("online")) {
      return "online";
    }

    // Use explicit type fields if available
    if (event.type) {
      return event.type;
    }
    if (event.eventType) {
      return event.eventType;
    }

    // Check for offline indicators
    if (
      event.place ||
      event.address ||
      (event.venue && !event.venue.toLowerCase().includes("online"))
    ) {
      return "offline";
    }

    return "online"; // default
  };

  const normalizeEventType = (eventType) => {
    const normalized = String(eventType || "").toLowerCase();
    if (normalized.includes("online")) return "online";
    if (normalized.includes("offline")) return "offline";
    return "online"; // default
  };

  const transformEventData = async (events) => {
    const transformedEvents = await Promise.all(
      events.map(async (event) => {
        const decryptedTrainer = event.trainerName
          ? await decryptWithSpaces(event.trainerName)
          : "";

        const eventType = normalizeEventType(determineEventType(event));

        return {
          ...event,
          title: event.eventTopicName || event.title || "",
          trainer: decryptedTrainer || event.trainer || "",
          date: event.startTime
            ? moment(event.startTime).format("DD MMM, YYYY")
            : event.date || "",
          time: event.startTime
            ? moment(event.startTime).format("hh:mm A")
            : event.time || "",
          type: eventType,
        };
      }),
    );
    return transformedEvents;
  };

  // Compute filtered arrays based on event dates
  // Treat events as past when startTime < now - t1 (t1 = 1 day)
  const computeFilteredArrays = (storeData) => {
    const nowMs = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000; // 1 day
    const pastThresholdMs = nowMs - oneDayMs; // events older than this are past

    const todayStartMs = new Date().setHours(0, 0, 0, 0);
    const momenttoday = moment(new Date()).format("DD-MM-YYYY");

    const safeTime = (ev) => {
      const t = new Date(ev.startTime).getTime();
      return isNaN(t) ? 0 : t;
    };

    // Past: strictly older than 1 day
    const filteredpastData = storeData.filter((event) => {
      return safeTime(event) > 0 && safeTime(event) < pastThresholdMs;
    });

    // Today: same calendar day as today and not older than the 1-day threshold
    const filteredtodaysData = storeData.filter((event) => {
      const evDateStr = moment(new Date(event.startTime)).format("DD-MM-YYYY");
      return evDateStr === momenttoday && safeTime(event) >= pastThresholdMs;
    });

    const filteredTomorrowData = [];

    // Upcoming: events with date > today (future calendar days)
    const filteredUpcomingData = storeData.filter((event) => {
      const evDateMs = new Date(event.startTime).setHours(0, 0, 0, 0);
      return evDateMs > todayStartMs;
    });

    // Sort results: past = most recent first, today/upcoming = soonest first
    filteredpastData.sort((a, b) => safeTime(b) - safeTime(a));
    filteredtodaysData.sort((a, b) => safeTime(a) - safeTime(b));
    filteredUpcomingData.sort((a, b) => safeTime(a) - safeTime(b));

    return {
      filteredpastData,
      filteredtodaysData,
      filteredTomorrowData,
      filteredUpcomingData,
    };
  };

  const loadEvents = useCallback(async () => {
    // Create abort controller for this request
    const abortController = new AbortController();

    try {
      setLoading(true);
      setError(null);

      console.log("Fetching events with filters:", filters);

      const response = await fetchEventlist(
        filters.state,
        filters.city,
        filters.pincode,
        filters.date,
        filters.subject,
      );

      // Check if request was aborted
      if (abortController.signal.aborted) {
        console.log("Request aborted");
        return;
      }

      console.log("API Response:", response);

      if (response && Array.isArray(response) && response.length > 0) {
        console.log("Response data array length:", response.length);

        // Transform API data first
        const transformedData = await transformEventData(response);

        // Check again after async transformation
        if (abortController.signal.aborted) {
          console.log("Request aborted during transformation");
          return;
        }

        // Use computeFilteredArrays to categorize events
        const { filteredpastData, filteredtodaysData, filteredUpcomingData } =
          computeFilteredArrays(transformedData);

        // Log event types for debugging
        const eventTypes = transformedData.map((e) => ({
          id: e.id,
          type: e.type,
          title: e.title,
        }));
        console.log("Event types:", eventTypes);

        setPastEvents(filteredpastData);
        setTodaysEvents(filteredtodaysData);
        setUpcomingEvents(filteredUpcomingData);
      } else {
        console.warn("No data in response:", response);
        // Don't set error for empty results
        setPastEvents([]);
        setTodaysEvents([]);
        setUpcomingEvents([]);
      }
    } catch (err) {
      // Don't show error if request was aborted
      if (abortController.signal.aborted) {
        console.log("Request aborted in error handler");
        return;
      }

      console.error("Error fetching events:", err);
      console.error("Error details:", {
        message: err.message,
        response: err.response,
        stack: err.stack,
      });
      setError(
        `Failed to load events: ${err.message || "Please try again later."}`,
      );
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }

    // Return cleanup function
    return () => {
      abortController.abort();
    };
  }, [filters]);

  useEffect(() => {
    const cleanup = loadEvents();

    // Cleanup on unmount or when loadEvents changes
    return () => {
      if (cleanup && typeof cleanup.then === "function") {
        cleanup.then((cleanupFn) => {
          if (cleanupFn) cleanupFn();
        });
      }
    };
  }, [loadEvents]);

  // Clean up state when component unmounts to ensure fresh data on return
  useEffect(() => {
    return () => {
      // Clear events data on unmount
      setUpcomingEvents([]);
      setPastEvents([]);
      setTodaysEvents([]);
    };
  }, []);

  // Auto-dismiss error after 1 second
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [error]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showFilterDropdown &&
        !event.target.closest(".filter-dropdown-wrapper") &&
        !event.target.closest(".MuiPickersPopper-root") &&
        !event.target.closest(".MuiDialog-root") &&
        !event.target.closest(".MuiPopper-root") &&
        !event.target.closest(".MuiMenu-root") &&
        !event.target.closest(".MuiPaper-root") &&
        !event.target.closest(".MuiMenuItem-root") &&
        !event.target.closest(".MuiSelect-popper") &&
        !event.target.closest(".MuiAutocomplete-popper") &&
        !event.target.closest("[role='listbox']") &&
        !event.target.closest("[role='option']") &&
        !event.target.closest("[role='combobox']") &&
        !event.target.closest("[id*='menu']") &&
        !event.target.closest("[class*='dropdown']") &&
        !event.target.closest("[class*='options']") &&
        !event.target.closest("[class*='select']")
      ) {
        setShowFilterDropdown(false);
      }
    };

    if (showFilterDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilterDropdown]);

  const getFilteredEvents = (events) => {
    if (!events || events.length === 0) return [];

    console.log(`Filtering ${events.length} events for tab: ${activeTab}`);

    if (activeTab === "all") return events;

    const filtered = events.filter((event) => {
      const eventType = (event.type || "").toLowerCase();
      const matches = eventType === activeTab.toLowerCase();
      console.log(
        `Event "${event.title}" type: "${event.type}" matches "${activeTab}": ${matches}`,
      );
      return matches;
    });

    console.log(`Filtered result: ${filtered.length} events`);
    return filtered;
  };

  // Combine today's events with upcoming events for display
  const allUpcomingEvents = [...todaysEvents, ...upcomingEvents];

  console.log(`Active Tab: ${activeTab}`);
  console.log(
    `Today's events: ${todaysEvents.length}, Upcoming: ${upcomingEvents.length}, Combined: ${allUpcomingEvents.length}`,
  );
  console.log(`Past events: ${pastEvents.length}`);

  const displayUpcomingEvents = getFilteredEvents(allUpcomingEvents);
  const displayPastEvents = getFilteredEvents(pastEvents);

  // Limits for landing page previews
  const UPCOMING_LIMIT = 3;
  const PAST_LIMIT = 4;

  const shownUpcomingEvents = showAllUpcoming
    ? displayUpcomingEvents
    : displayUpcomingEvents.slice(0, UPCOMING_LIMIT);

  const shownPastEvents = showAllPast
    ? displayPastEvents
    : displayPastEvents.slice(0, PAST_LIMIT);

  const hasNoEvents =
    displayUpcomingEvents.length === 0 && displayPastEvents.length === 0;

  // Helper functions to reduce cognitive complexity
  const getTabClassName = (tabValue) => {
    const isActive = activeTab === tabValue ? "active" : "";
    return `filter-tab ${isActive}`;
  };

  const getFilterButtonClassName = () => {
    const statusClass = isFilterApplied ? "filter-applied" : "";
    return `filter-btn desktop-filter ${statusClass}`;
  };

  const getFilterIconColor = () => {
    return isFilterApplied ? "#FFFFFF" : "#003A5D";
  };

  const getFilterButtonText = () => {
    return isFilterApplied ? "Filter Applied" : "Filter";
  };

  const getNoEventsFilterText = () => {
    return activeTab !== "all" ? activeTab : "";
  };

  const getUpcomingToggleText = () => {
    return showAllUpcoming ? "Show Less" : "View All Upcoming Events";
  };

  const getPastToggleText = () => {
    return showAllPast ? "Show Less" : "View All Past Events";
  };

  return (
    <React.Fragment>
      <div className="events-landing">
        {/* Hero Section */}
        <section className="events-hero">
          <div className="events-hero-bg">
            <div className="hero-wave"></div>
          </div>

          {/* Custom Header */}
          <DspHeader />

          <div className="container-lg">
            <div className="row">
              <div className="col-lg-7  herodiv1">
                <div className="hero-content">
                  <h1 className="hero-title">
                    Learn About<br />
                    <span className="text-highlight">Mutual Funds</span>
                    <div className="hero-title-underline"></div>
                  </h1>
                  <p className="hero-description">
                    Discover webinars and offline events to learn about mutual
                    funds and make better investment decisions.
                  </p>
                  <div className="hero-tags">
                    <div className="hero-tag">
                      <div className="hero-tag-icon-circle">
                        <MenuBookOutlinedIcon />
                      </div>
                      <span className="hero-tag-text">Gain valuable insight</span>
                    </div>
                    <div className="hero-tag-divider"></div>
                    <div className="hero-tag">
                      <div className="hero-tag-icon-circle">
                        <GroupsOutlinedIcon />
                      </div>
                      <span className="hero-tag-text">Empower yourself financially</span>
                    </div>
                    <div className="hero-tag-divider"></div>
                    <div className="hero-tag">
                      <div className="hero-tag-icon-circle">
                        <ChatBubbleOutlineIcon />
                      </div>
                      <span className="hero-tag-text">Interact and engage</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-5  herodiv2">
                <div className="hero-illustration">
                  {/* Illustration placeholder */}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Events Section */}
        <section className="events-section">
          <div className="events-bg-circles">
            <div className="circle circle-1"></div>
            <div className="circle circle-2"></div>
          </div>

          <div className="container-lg">
            {/* Filter Bar */}
            <div className="events-filter-bar">
              {/* Desktop Tabs */}
              <div className="filter-tabs desktop-only">
                <button
                  className={getTabClassName("all")}
                  onClick={() => setActiveTab("all")}
                >
                  All Events
                </button>
                <button
                  className={getTabClassName("online")}
                  onClick={() => setActiveTab("online")}
                >
                  Online
                </button>
                <button
                  className={getTabClassName("offline")}
                  onClick={() => setActiveTab("offline")}
                >
                  Offline
                </button>
              </div>

              {/* Mobile Dropdown */}
              <div className="filter-dropdown-select mobile-only">
                <CustomSelect
                  value={activeTab}
                  onChange={(e) => setActiveTab(e.target.value)}
                  options={[
                    { label: "All Events", value: "all" },
                    { label: "Online", value: "online" },
                    { label: "Offline", value: "offline" },
                  ]}
                  minWidth="100%"
                />
              </div>

              <div className="filter-actions">
                {/* Desktop Search */}
                <div className="search-input-wrap desktop-search">
                  <SearchIcon
                    className="search-icon-left"
                    style={{ color: "#333333" }}
                  />
                  <input
                    className="search-input"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="search-clear-btn"
                      onClick={() => setSearchQuery("")}
                      aria-label="Clear search"
                    >
                      <CloseIcon
                        className="clear-icon"
                        style={{ color: "#333333" }}
                      />
                    </button>
                  )}
                </div>

                {/* Mobile Search Icon */}
                <button
                  className="icon-btn mobile-search-btn mobile-only"
                  onClick={() => {
                    setShowMobileSearch(!showMobileSearch);
                    if (!showMobileSearch) {
                      setTimeout(() => {
                        const input = document.querySelector(
                          ".mobile-search-input",
                        );
                        input?.focus();
                      }, 100);
                    }
                  }}
                  aria-label="Search"
                >
                  <SearchIcon style={{ color: "#333333" }} />
                </button>

                <div className="filter-dropdown-wrapper">
                  {/* Desktop Filter Button */}
                  <button
                    className={getFilterButtonClassName()}
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  >
                    {!isFilterApplied && (
                      <FilterAltOutlinedIcon
                        className="filter-icon"
                        style={{
                          color: getFilterIconColor(),
                        }}
                      />
                    )}
                    <div className="filter-text">
                      {getFilterButtonText()}
                    </div>

                    {isFilterApplied && (
                      <ClearIcon
                        className="close-icon"
                        style={{ color: "#003A5D" }}
                        onClick={handleClearFilter}
                      />
                    )}
                  </button>

                  {/* Mobile Filter Icon Button */}
                  <button
                    className={`icon-btn mobile-filter-btn mobile-only ${isFilterApplied ? "filter-applied" : ""}`}
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    aria-label="Filter"
                  >
                    <FilterAltOutlinedIcon
                      className="filter-icon"
                      style={{ color: "#333333" }}
                    />
                  </button>
                  {showFilterDropdown && (
                    <>
                      <div
                        className="filter-backdrop"
                        onClick={() => setShowFilterDropdown(false)}
                      ></div>
                      <div
                        className="filter-dropdown"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="filter-dropdown-header">
                          <h3 className="filter-dropdown-title">Filter by:</h3>
                          <button
                            className="filter-close-btn"
                            onClick={() => setShowFilterDropdown(false)}
                            aria-label="Close filter"
                          >
                            <CloseIcon className="filter-close-icon" />
                          </button>
                        </div>
                        <div className="filter-dropdown-body">
                          <div className="filter-row">
                            <div
                              className="filter-field"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <label className="filter-label">Month</label>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DesktopDatePicker
                                  displayStaticWrapperAs="desktop"
                                  clearable
                                  views={["month", "year"]}
                                  inputFormat="MMM-YYYY"
                                  openTo="month"
                                  minDate={minDateRange}
                                  maxDate={maxDateRange}
                                  value={tempFilters.month}
                                  onChange={(newValue) => {
                                    setTempFilters({
                                      ...tempFilters,
                                      month: newValue,
                                    });
                                  }}
                                  renderInput={({
                                    inputRef,
                                    inputProps,
                                    InputProps,
                                  }) => (
                                    <Box>
                                      <div className="filter-date-wrapper">
                                        <input
                                          {...inputProps}
                                          ref={inputRef}
                                          type="text"
                                          className="filter-input"
                                          placeholder="Select month"
                                          readOnly
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                        {InputProps?.endAdornment}
                                      </div>
                                    </Box>
                                  )}
                                />
                              </LocalizationProvider>
                            </div>
                            <div
                              className="filter-field"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <CustomizedDropdown
                                label="State"
                                placeholder="Choose an option"
                                value={tempFilters.state}
                                onChange={(e) => {
                                  const selectedState = e.target.value;
                                  setTempFilters({
                                    ...tempFilters,
                                    state: selectedState,
                                    city: "",
                                    pincode: "",
                                  });
                                  if (selectedState) {
                                    handlefetchCityList(selectedState);
                                  } else {
                                    setCityOptions([]);
                                    setPinOptions([]);
                                  }
                                }}
                                options={stateOptions}
                                minWidth="100%"
                              />
                            </div>
                          </div>
                          <div className="filter-row">
                            <div
                              className="filter-field"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <CustomizedDropdown
                                label="District"
                                placeholder="Choose an option"
                                value={tempFilters.city}
                                onChange={(e) => {
                                  const selectedCity = e.target.value;
                                  setTempFilters({
                                    ...tempFilters,
                                    city: selectedCity,
                                    pincode: "",
                                  });
                                  if (selectedCity) {
                                    // Find the district in stored data and extract pincodes
                                    const district = districtData.find(
                                      (d) => d.districtName === selectedCity,
                                    );
                                    if (district && district.pincode) {
                                      const pincodeOptions =
                                        district.pincode.map((pin) => ({
                                          value: pin,
                                          label: pin,
                                        }));
                                      setPinOptions(pincodeOptions);
                                    } else {
                                      setPinOptions([]);
                                    }
                                  } else {
                                    setPinOptions([]);
                                  }
                                }}
                                options={cityOptions}
                                minWidth="100%"
                                disabled={!tempFilters.state}
                              />
                            </div>
                            <div
                              className="filter-field"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <CustomizedDropdown
                                label="Pincode"
                                placeholder="Choose an option"
                                value={tempFilters.pincode}
                                onChange={(e) => {
                                  setTempFilters({
                                    ...tempFilters,
                                    pincode: e.target.value,
                                  });
                                }}
                                options={pinOptions}
                                minWidth="100%"
                                disabled={!tempFilters.city}
                              />
                            </div>
                          </div>
                        </div>
                        <div
                          className="filter-dropdown-footer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            className="clear-filter-btn"
                            onClick={handleClearFilter}
                          >
                            Clear Filter
                          </button>
                          <button
                            className="apply-filter-btn"
                            onClick={handleApplyFilter}
                          >
                            Apply Filter
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile Search Input */}
            {showMobileSearch && (
              <div className="mobile-search-container mobile-only">
                <SearchIcon className="mobile-search-icon" />
                <input
                  type="text"
                  className="mobile-search-input"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="mobile-search-clear"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                  >
                    <CloseIcon />
                  </button>
                )}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {/* No Events Message */}
            {!error && !loading && hasNoEvents && (
              <div className="alert alert-info text-center" role="alert">
                <h4>No Events Found</h4>
                <p>
                  There are currently no {getNoEventsFilterText()}{" "}
                  events available. Please check back later!
                </p>
              </div>
            )}

            {/* Upcoming Events */}
            {loading ? (
              <SkeletonLoader count={6} />
            ) : (
              <div className="events-list-section">
                <div className="section-title">
                  {displayUpcomingEvents.length} Upcoming Events
                </div>

                {shownUpcomingEvents.length > 0 ? (
                  <>
                    <div className="row">
                      {shownUpcomingEvents.map((event) => (
                        <div key={event.id} className="col-lg-4 col-md-6 mb-4">
                          <EventCardLarge event={event} />
                        </div>
                      ))}
                    </div>

                    {displayUpcomingEvents.length > UPCOMING_LIMIT && (
                      <div className="text-center mt-2">
                        <button
                          className="view-all-btn"
                          onClick={() => setShowAllUpcoming(!showAllUpcoming)}
                        >
                          {getUpcomingToggleText()}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-events-message">
                    <p>No upcoming events found. Please check back later!</p>
                  </div>
                )}
              </div>
            )}

            {/* Past Events */}
            {loading ? (
              <SkeletonSmall count={4} />
            ) : (
              <div className="events-list-section mt-5">
                <div className="section-title">Past Events</div>

                {shownPastEvents.length > 0 ? (
                  <>
                    <div className="row">
                      {shownPastEvents.map((event) => (
                        <div key={event.id} className="col-lg-3 col-md-6 mb-4">
                          <EventCardSmall event={event} />
                        </div>
                      ))}
                    </div>

                    {displayPastEvents.length > PAST_LIMIT && (
                      <div className="text-center mt-2">
                        <button
                          className="view-all-btn"
                          onClick={() => setShowAllPast(!showAllPast)}
                        >
                          {getPastToggleText()}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-events-message">
                    <p>No past events found.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Custom Footer */}
        <DspFooter />
      </div>
    </React.Fragment>
  );
};

export default EventsLanding;
